import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import CallSplitRoundedIcon from "@mui/icons-material/CallSplitRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import Seo from "../../components/Seo";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";

export default function ParallelProcessingLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/parallel-processing";
  const pageTitle = "Parallel Processing in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what parallel processing is, how dry and processed signals are blended together, and how parallel compression, saturation, distortion, reverb, and other techniques are used in mixing.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "parallel processing",
          "parallel compression",
          "what is parallel processing",
          "parallel saturation",
          "parallel distortion",
          "new york compression",
          "parallel mixing",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Parallel processing" },
          { "@type": "Thing", name: "Parallel compression" },
          { "@type": "Thing", name: "Saturation" },
          { "@type": "Thing", name: "Distortion" },
          { "@type": "Thing", name: "Mixing" },
          { "@type": "Thing", name: "Audio engineering" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Parallel Processing",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Parallel processing blends a dry signal with a separately processed version of that same signal.",
      icon: <CallSplitRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It lets you add intensity, tone, or excitement without fully replacing the original sound.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Compression, saturation, distortion, EQ, ambience effects, drums, vocals, bass, synths, and full mix techniques.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const concepts = [
    {
      name: "Dry Signal",
      description:
        "The original unprocessed sound that keeps its natural tone, dynamics, and transients.",
    },
    {
      name: "Processed Signal",
      description:
        "A second version of the sound that is compressed, saturated, distorted, EQ’d, or otherwise shaped more heavily.",
    },
    {
      name: "Blend",
      description:
        "The dry and processed versions are mixed together, letting you keep clarity while adding character or density.",
    },
    {
      name: "Controlled Intensity",
      description:
        "Parallel processing often makes heavy processing feel more usable because you do not have to commit to 100% of the effect.",
    },
  ];

  const commonTypes = [
    {
      name: "Parallel Compression",
      description:
        "One of the most common forms. Heavy compression is blended underneath the original signal for more density and punch.",
    },
    {
      name: "Parallel Saturation",
      description:
        "Adds warmth, harmonic richness, or edge while keeping the main source cleaner and more intact.",
    },
    {
      name: "Parallel Distortion",
      description:
        "Blends grit and aggression without fully destroying the source tone or articulation.",
    },
    {
      name: "Parallel EQ",
      description:
        "Shapes or exaggerates a frequency range on the processed copy while preserving the original tonal balance.",
    },
    {
      name: "Parallel Reverb or Delay",
      description:
        "A classic send-effect approach where ambience is blended with the dry source rather than inserted fully inline.",
    },
    {
      name: "Parallel Special Effects",
      description:
        "Can include bitcrushing, modulation, filtering, widening, or other treatments blended in for extra character.",
    },
  ];

  const commonUses = [
    "Making drums feel bigger and more energetic without losing transient impact",
    "Adding body to vocals while preserving clarity and natural expression",
    "Helping bass feel denser and more audible without overwhelming the mix",
    "Blending in distortion or saturation for excitement while keeping note definition",
    "Using parallel ambience to add space without pushing the source too far back",
    "Making synths and sound design layers feel more complex and textured",
  ];

  const controls = [
    {
      name: "Send Level",
      description:
        "Controls how much of the original signal is fed to the parallel processing path.",
    },
    {
      name: "Return Level / Blend",
      description:
        "Controls how much of the processed signal is mixed back with the dry signal.",
    },
    {
      name: "Processor Settings",
      description:
        "The parallel chain can be processed much more aggressively than you would normally use inline.",
    },
    {
      name: "EQ Before or After",
      description:
        "Filtering the parallel path can focus the effect where it is most useful instead of affecting the whole spectrum equally.",
    },
    {
      name: "Dynamics and Tone Balance",
      description:
        "Fine adjustments decide whether the effect adds energy, thickness, brightness, grit, or space.",
    },
    {
      name: "Timing and Phase Alignment",
      description:
        "Important when blending paths, because misalignment can weaken the sound instead of improving it.",
    },
  ];

  const bestPractices = [
    "Push the parallel chain harder than you would in a normal insert, then blend it back carefully.",
    "Always compare the blend in context, because the parallel signal should support the mix rather than distract from it.",
    "Filter the parallel path if needed so low-end mud or harsh highs do not build up unnecessarily.",
    "Check for phase or latency issues if the blend starts sounding thinner instead of fuller.",
    "Use parallel processing when you want more intensity without sacrificing the identity of the original sound.",
  ];

  const relatedLinks = [
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Start with the basics of compression before exploring parallel compression techniques.",
    },
    {
      title: "Bus Processing",
      to: "/learning/bus-processing",
      description:
        "See how parallel chains often work alongside group and bus workflows in a mix.",
    },
    {
      title: "Saturation",
      to: "/learning/saturation",
      description:
        "Learn how saturation becomes especially flexible when used in parallel.",
    },
    {
      title: "Distortion",
      to: "/learning/distortion",
      description:
        "Explore how parallel distortion can add aggression while keeping the original sound intact.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Parallel Processing | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Parallel Processing in Music Production
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Parallel processing is one of the most powerful ways to get intensity without losing the core identity of a sound.
                      Instead of fully replacing the original signal with heavy processing, you blend the processed version back in alongside the dry source.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      That makes it possible to add density, grit, width, tone, or ambience while preserving clarity, punch, and natural detail.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5, lineHeight: 1.2 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.55 }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    What is parallel processing?
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Parallel processing means creating a second signal path for a sound, processing that copy, and then blending it with the original.
                    The original dry signal keeps its natural behavior, while the processed path adds extra tone, density, excitement, or space.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    This is different from standard insert processing, where the original signal is replaced by the processed version.
                    With parallel processing, the key advantage is balance. You can keep the best parts of the original while bringing in only as much of the effect as you want.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    That is why parallel techniques are so common in modern mixing. They are flexible, musical, and often easier to control than fully committing to aggressive processing.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Core parallel processing concepts
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {concepts.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Common types of parallel processing
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {commonTypes.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Common control points
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controls.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                              {item.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Parallel processing vs insert processing
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        Replacement vs blend
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Insert processing replaces the original signal path with the processed result. Parallel processing blends the processed version with the original.
                        That difference is why parallel workflows are so good for adding weight and intensity while keeping natural transients and tone.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Neither approach is universally better. Insert processing is often simpler and more direct. Parallel processing is often more flexible when heavy processing would otherwise sound too destructive.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        Common uses in mixing
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {commonUses.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <LibraryMusicRoundedIcon sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                      Parallel processing is especially useful when the original sound already has something valuable you do not want to lose.
                      It lets you add attitude without flattening the source completely.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningAmberRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        Common mistakes
                      </Typography>
                    </Stack>

                    <Stack spacing={1.35}>
                      {[
                        "Blending too much processed signal and losing the original clarity or balance.",
                        "Ignoring phase or latency problems that can weaken the combined result.",
                        "Using a parallel path without EQ control and building up mud or harshness.",
                        "Treating parallel processing like a shortcut instead of making sure the base sound already works well.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <WarningAmberRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Best practices
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {bestPractices.map((item, index) => (
                      <Paper key={index} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LayersRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Why parallel processing works so well
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.35}>
                      {[
                        "It keeps the original sound present while adding extra energy underneath or alongside it.",
                        "It makes aggressive processing more usable and more musical.",
                        "It gives fine control over intensity because the blend can be adjusted continuously.",
                        "It is one of the best ways to add size, density, and attitude without destroying source identity.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <LayersRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Related learning topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Final thoughts
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Parallel processing is valuable because it gives you a middle ground between untouched and overprocessed.
                    It lets you keep what is working in the source while adding extra power, tone, or excitement around it.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Used carefully, it can make a mix feel bigger, richer, and more alive without sacrificing clarity and musicality.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}